import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Loader2, Eye, EyeOff, Star, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Figurine, Category } from '@/lib/types';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import AdminMediaManager from './AdminMediaManager';
import { useFigurineImages } from '@/hooks/useFigurineImages';

type FigurineForm = {
  title: string; slug: string; description: string; category_slug: string;
  price_pln: string; image_url: string; tags: string; is_published: boolean; is_featured: boolean;
};
const EMPTY_FORM: FigurineForm = { title: '', slug: '', description: '', category_slug: '', price_pln: '', image_url: '', tags: '', is_published: false, is_featured: false };

function toSlug(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

export default function AdminFigurines() {
  const [figurines, setFigurines] = useState<Figurine[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'info' | 'media'>('info');
  const [editing, setEditing] = useState<Figurine | null>(null);
  const [form, setForm] = useState<FigurineForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { items: mediaItems, reload: reloadMedia } = useFigurineImages(editing?.id ?? '');

  const load = async () => {
    setLoading(true);
    const [{ data: figs }, { data: cats }] = await Promise.all([
      supabase.from('figurines').select('*').order('created_at', { ascending: false }),
      supabase.from('categories').select('*').order('sort_order'),
    ]);
    setFigurines((figs ?? []) as Figurine[]);
    setCategories((cats ?? []) as Category[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setModalTab('info'); setModalOpen(true); };
  const openEdit = (f: Figurine) => {
    setEditing(f);
    setForm({ title: f.title, slug: f.slug, description: f.description ?? '', category_slug: f.category_slug ?? '', price_pln: f.price_pln?.toString() ?? '', image_url: f.image_url ?? '', tags: f.tags.join(', '), is_published: f.is_published, is_featured: f.is_featured });
    setModalTab('info');
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(), slug: form.slug.trim() || toSlug(form.title),
      description: form.description.trim() || null,
      category_slug: form.category_slug || null,
      price_pln: form.price_pln ? parseFloat(form.price_pln) : null,
      image_url: form.image_url.trim() || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      is_published: form.is_published, is_featured: form.is_featured,
      updated_at: new Date().toISOString(),
    };
    let error;
    if (editing) {
      ({ error } = await supabase.from('figurines').update(payload).eq('id', editing.id));
    } else {
      ({ error } = await supabase.from('figurines').insert(payload));
    }
    if (error) toast.error(error.message);
    else { toast.success(editing ? 'Figurka zaktualizowana!' : 'Figurka dodana!'); setModalOpen(false); load(); }
    setSaving(false);
  };

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Usunąć "${title}"? Tej operacji nie można cofnąć.`)) return;
    setDeleting(id);
    const { error } = await supabase.from('figurines').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Figurka usunięta.'); load(); }
    setDeleting(null);
  };

  const togglePublish = async (f: Figurine) => {
    const { error } = await supabase.from('figurines').update({ is_published: !f.is_published }).eq('id', f.id);
    if (!error) load(); else toast.error(error.message);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>FIGURKI</h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Zarządzaj kolekcją figurek</p>
        </div>
        <Button onClick={openAdd} className="gap-2 bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4" /> Dodaj figurkę
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'oklch(0.25 0.045 280)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'oklch(0.14 0.028 280)', borderBottom: '1px solid oklch(0.25 0.045 280)' }}>
                {['Zdjęcie', 'Tytuł', 'Kategoria', 'Cena', 'Status', 'Featured', 'Akcje'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid oklch(0.22 0.04 280)' }}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 rounded animate-pulse" style={{ background: 'var(--muted)', width: `${60 + j * 5}%` }} /></td>
                    ))}
                  </tr>
                ))
              ) : figurines.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Brak figurek. Dodaj pierwszą!</td></tr>
              ) : figurines.map((fig) => (
                <motion.tr
                  key={fig.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="group"
                  style={{ borderBottom: '1px solid oklch(0.20 0.035 280)', background: 'oklch(0.16 0.03 280)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.18 0.04 280)'}
                  onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.16 0.03 280)'}
                >
                  <td className="px-4 py-3">
                    {fig.image_url ? (
                      <img src={fig.image_url} alt={fig.title} className="w-10 h-10 rounded-lg object-cover border" style={{ borderColor: 'oklch(0.28 0.05 280)' }} />
                    ) : (
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'oklch(0.20 0.035 280)' }}>
                        <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{fig.title}</div>
                    <div className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fig.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'oklch(0.22 0.04 280)', color: 'var(--accent)', fontFamily: 'JetBrains Mono, monospace' }}>
                      {fig.category_slug ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-bold text-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {fig.price_pln != null ? `${Number(fig.price_pln).toFixed(2)} zł` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => togglePublish(fig)} className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full transition-all" style={{ background: fig.is_published ? 'oklch(0.65 0.26 150 / 0.15)' : 'oklch(0.22 0.04 280)', color: fig.is_published ? 'var(--chart-3)' : 'var(--muted-foreground)' }}>
                      {fig.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {fig.is_published ? 'Pub.' : 'Ukryta'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {fig.is_featured
                      ? <Star className="w-4 h-4" style={{ color: 'var(--chart-4)', fill: 'var(--chart-4)' }} />
                      : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(fig)} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(fig.id, fig.title)} disabled={deleting === fig.id} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                        {deleting === fig.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <>
            <motion.div key="bd" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50" style={{ background: 'oklch(0.05 0.01 280 / 0.85)' }} onClick={() => setModalOpen(false)} />
            <motion.div key="md" initial={{ opacity: 0, scale: 0.93, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.93, y: 16 }} transition={{ type: 'spring', stiffness: 320, damping: 30 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div className="pointer-events-auto w-full max-w-xl rounded-2xl border p-7 relative max-h-[90vh] overflow-y-auto" style={{ background: 'oklch(0.14 0.028 280)', borderColor: 'oklch(0.28 0.05 280)' }}>
                <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                <h2 className="text-xl font-black mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>{editing ? 'EDYTUJ FIGURKĘ' : 'NOWA FIGURKA'}</h2>

                {/* Tabs – Media only available when editing existing */}
                <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: 'oklch(0.12 0.025 280)' }}>
                  {[
                    { id: 'info', label: 'DANE' },
                    { id: 'media', label: `MEDIA ${editing ? `(${mediaItems.length})` : ''}`, disabled: !editing },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      disabled={!!tab.disabled}
                      onClick={() => setModalTab(tab.id as 'info' | 'media')}
                      className="flex-1 py-1.5 rounded-lg text-xs font-bold tracking-widest transition-all disabled:opacity-30"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        background: modalTab === tab.id ? 'var(--primary)' : 'transparent',
                        color: modalTab === tab.id ? 'white' : 'var(--muted-foreground)',
                      }}
                    >{tab.label}</button>
                  ))}
                </div>
                <form onSubmit={handleSave} className="space-y-4">
                  {modalTab === 'info' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>TYTUŁ *</Label>
                      <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value, slug: f.slug || toSlug(e.target.value) }))} placeholder="np. Cyborg Warrior X9" className="bg-card border-border/60 text-white placeholder:text-muted-foreground/50" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SLUG *</Label>
                      <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: toSlug(e.target.value) }))} placeholder="cyborg-warrior-x9" className="bg-card border-border/60 font-mono text-xs text-white placeholder:text-muted-foreground/50" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>KATEGORIA</Label>
                      <select value={form.category_slug} onChange={e => setForm(f => ({ ...f, category_slug: e.target.value }))} className="w-full h-10 px-3 rounded-lg border text-sm bg-card border-border/60 text-foreground">
                        <option value="">— brak —</option>
                        {categories.map(c => <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>OPIS</Label>
                      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Opis figurki..." rows={3} className="w-full px-3 py-2 rounded-lg border text-sm bg-card border-border/60 text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>CENA (zł)</Label>
                      <Input type="number" step="0.01" value={form.price_pln} onChange={e => setForm(f => ({ ...f, price_pln: e.target.value }))} placeholder="79.99" className="bg-card border-border/60 text-white placeholder:text-muted-foreground/50" />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground flex items-center gap-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        <ImageIcon className="w-3 h-3" /> ZDJĘCIE FIGURKI
                      </Label>
                      <ImageUpload
                        value={form.image_url}
                        onChange={url => setForm(f => ({ ...f, image_url: url }))}
                        figurineSlug={form.slug || 'figurine'}
                      />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>TAGI (oddziel przecinkami)</Label>
                      <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="sci-fi, robot, metal" className="bg-card border-border/60 text-white placeholder:text-muted-foreground/50" />
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input type="checkbox" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} className="w-4 h-4 accent-primary" />
                      <span className="text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Opublikowana</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="w-4 h-4 accent-primary" />
                      <span className="text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Featured (wyróżniona)</span>
                    </label>
                  </div>
                  )}

                  {/* MEDIA tab */}
                  {modalTab === 'media' && editing && (
                    <AdminMediaManager
                      figurineId={editing.id}
                      figurineSlug={editing.slug}
                      items={mediaItems}
                      onReload={reloadMedia}
                    />
                  )}

                  {/* Actions */}
                  {modalTab === 'info' && (
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">Anuluj</Button>
                    <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Zapisz zmiany' : 'Dodaj figurkę')}
                    </Button>
                  </div>
                  )}
                  {modalTab === 'media' && (
                  <div className="pt-2">
                    <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="w-full">Zamknij</Button>
                  </div>
                  )}
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
